import React from "react";

const ModalOverlay = ({ children }) => (
  <div
    className="position-fixed d-flex align-items-center justify-content-center"
    style={{
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0,0,0,0.5)",
      zIndex: 10000,
    }}
  >
    <div
      className="bg-white rounded shadow p-4"
      style={{ maxWidth: 520, width: "90%" }}
    >
      {children}
    </div>
  </div>
);

const WarningBox = ({ count, text }) => (
  <div
    className="p-3 rounded mb-3"
    style={{
      background: "#fefce8",
      border: "1px solid #fde68a",
      fontSize: 13,
      color: "#92400e",
    }}
  >
    <i className="fa fa-exclamation-triangle mr-2" />
    <strong>{count} inscription(s)</strong> {text}
  </div>
);

const OverwriteCheckbox = ({ checked, onChange, label, description }) => (
  <label
    className="d-flex align-items-start p-3 rounded mb-3"
    style={{
      background: checked ? "#fef3c7" : "#f8fafc",
      border: checked ? "1px solid #fde68a" : "1px solid #e2e8f0",
      gap: 10,
      cursor: "pointer",
      transition: "all 0.15s ease",
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{
        width: 16,
        height: 16,
        marginTop: 2,
        cursor: "pointer",
        accentColor: "#f59e0b",
      }}
    />
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
        {label}
      </div>
      <div style={{ fontSize: 12, color: "#64748b" }}>{description}</div>
    </div>
  </label>
);

const InfoBox = ({ willSendCount }) => (
  <div
    className="p-2 rounded mb-4"
    style={{
      background: "#f0fdf4",
      border: "1px solid #86efac",
      fontSize: 13,
      color: "#166534",
    }}
  >
    <i className="fa fa-info-circle mr-2" />
    <strong>{willSendCount} inscription(s)</strong> seront traitées.
  </div>
);

const ModalActions = ({ onCancel, onConfirm, isSaving, willSendCount }) => (
  <div className="d-flex justify-content-end" style={{ gap: 10 }}>
    <button
      className="btn btn-secondary"
      onClick={onCancel}
      disabled={isSaving}
    >
      Annuler
    </button>
    <button
      className="btn btn-success"
      onClick={onConfirm}
      disabled={isSaving || willSendCount === 0}
    >
      {isSaving ? (
        <i className="fa fa-spinner fa-spin mr-2" />
      ) : (
        <i className="fa fa-check mr-2" />
      )}
      Confirmer ({willSendCount})
    </button>
  </div>
);

// Modale de validation en masse des propositions RU
export const BulkRuValidateModal = ({
  inscriptions,
  selectedIds,
  overwriteExisting,
  setOverwriteExisting,
  isBulkSaving,
  onConfirm,
  onCancel,
}) => {
  const selectedInscriptions = inscriptions.filter((i) =>
    selectedIds.includes(i.id),
  );
  const modifiedCount = selectedInscriptions.filter(
    (i) => i.decisionId && i.decisionId !== i.selectedProposal,
  ).length;
  const pendingOrRuCount = selectedInscriptions.length - modifiedCount;
  const willSendCount = overwriteExisting
    ? selectedInscriptions.length
    : pendingOrRuCount;

  return (
    <ModalOverlay>
      <h5
        className="mb-3"
        style={{ fontSize: 18, color: "#1e293b", fontWeight: 700 }}
      >
        <i
          className="fa fa-check-circle mr-2"
          style={{ color: "#22c55e" }}
        />
        Validation des propositions RU
      </h5>
      <p style={{ fontSize: 14, color: "#475569" }}>
        Les propositions initiales du référent unique (RU) seront appliquées
        comme décision finale pour les{" "}
        <strong>{selectedInscriptions.length} allocataire(s)</strong>{" "}
        sélectionné(s).
      </p>

      {modifiedCount > 0 && (
        <WarningBox
          count={modifiedCount}
          text="ont déjà une décision différente de la proposition du RU."
        />
      )}

      {modifiedCount > 0 && (
        <OverwriteCheckbox
          checked={overwriteExisting}
          onChange={setOverwriteExisting}
          label="Écraser les décisions déjà modifiées"
          description={`Si décoché, seules les ${pendingOrRuCount} inscription(s) sans décision ou déjà sur la proposition RU seront traitées.`}
        />
      )}

      <InfoBox willSendCount={willSendCount} />
      <ModalActions
        onCancel={onCancel}
        onConfirm={onConfirm}
        isSaving={isBulkSaving}
        willSendCount={willSendCount}
      />
    </ModalOverlay>
  );
};

// Modale de validation en masse des accords directeur
export const BulkDirectorAccordModal = ({
  inscriptions,
  selectedIds,
  directorDecisions,
  overwriteDirectorExisting,
  setOverwriteDirectorExisting,
  isBulkDirectorSaving,
  onConfirm,
  onCancel,
}) => {
  const selectedInscriptions = inscriptions.filter((i) =>
    selectedIds.includes(i.id),
  );
  const nonAccordCount = selectedInscriptions.filter(
    (i) =>
      directorDecisions[i.id] &&
      directorDecisions[i.id].decision !== "ACCORD",
  ).length;
  const accordOrPendingCount = selectedInscriptions.length - nonAccordCount;
  const willSendCount = overwriteDirectorExisting
    ? selectedInscriptions.length
    : accordOrPendingCount;

  return (
    <ModalOverlay>
      <h5
        className="mb-3"
        style={{ fontSize: 18, color: "#1e293b", fontWeight: 700 }}
      >
        <i
          className="fa fa-check-circle mr-2"
          style={{ color: "#22c55e" }}
        />
        Accorder les sélectionnées
      </h5>
      <p style={{ fontSize: 14, color: "#475569" }}>
        La décision directeur <strong>ACCORD</strong> sera appliquée pour
        les{" "}
        <strong>{selectedInscriptions.length} allocataire(s)</strong>{" "}
        sélectionné(s).
      </p>

      {nonAccordCount > 0 && (
        <WarningBox
          count={nonAccordCount}
          text="ont déjà une décision directeur différente de ACCORD."
        />
      )}

      {nonAccordCount > 0 && (
        <OverwriteCheckbox
          checked={overwriteDirectorExisting}
          onChange={setOverwriteDirectorExisting}
          label="Écraser les décisions déjà enregistrées"
          description={`Si décoché, seules les ${accordOrPendingCount} inscription(s) sans décision ou déjà en ACCORD seront traitées.`}
        />
      )}

      <InfoBox willSendCount={willSendCount} />
      <ModalActions
        onCancel={onCancel}
        onConfirm={onConfirm}
        isSaving={isBulkDirectorSaving}
        willSendCount={willSendCount}
      />
    </ModalOverlay>
  );
};
